import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from '@heroicons/react/24/outline';
import { type ChangeEvent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import FileUploadButton from '@/components/FileUploadButton';
import meetingDate from '@/data/meetingDate.ts';
import type { RootState } from '../../../store';
import { fetchOpportunity } from '../../../store/Opportunity';
import { set } from '../../../store/Opportunity/BasicInfo';
import Charts from './Charts';
import ProjectOverview from './ProjectOverview';
import ProjectTeam from './ProjectTeam';

export default function Opportunity() {
  const params = useParams<string>();
  const dispatch = useDispatch();
  const opportunityStateRoot = useSelector((state: RootState) => state.opportunity.opportunityMain);

  useEffect(() => {
    dispatch(fetchOpportunity(params.opportunityId!) as any);
  }, [params.opportunityId, dispatch]);

  const changeHandler = (e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
    dispatch(set({ [e.target.name]: e.target.value }));
  };

  let dashboard = (
    <div className='container mx-auto'>
      <div className='border border-gray-300 shadow rounded-md p-4 w-full mx-auto'>
        <div className='animate-pulse flex space-x-4 my-4'>
          <div className='w-1/4 space-y-4 py-1'>
            <div className='h-4 bg-gray-400 rounded'></div>
            <div className='space-y-2'>
              <div className='h-4 bg-gray-400 rounded w-3/4'></div>
            </div>
            <div className='space-y-2'>
              <div className='h-4 bg-gray-400 rounded w-5/6'></div>
            </div>
          </div>

          <div className='w-1/4 space-y-4 py-1'></div>

          <div className='w-1/4 space-y-4 py-1'>
            <div className='space-y-2'>
              <div className='h-48 bg-gray-400 rounded'></div>
            </div>
          </div>

          <div className='w-1/4 space-y-4 py-1'>
            <div className='space-y-2'>
              <div className='h-48 bg-gray-400 rounded'></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (opportunityStateRoot.fetchError) {
    return (
      <div className='container mx-auto my-16'>
        <div className='prose'>
          <h1>Sorry! We couldn't find this opportunity.</h1>
        </div>
      </div>
    );
  }

  if (!opportunityStateRoot.fetching) {
    dashboard = (
      <div className='container mx-auto'>
        <ProjectOverview opportunityState={opportunityStateRoot} />
        <Charts />
        <ProjectTeam opportunityState={opportunityStateRoot} />
        <div>
          <h2 className='font-bold'>Sourcing Tasks</h2>
          <div className='mt-2'>
            <table className='table-auto w-full'>
              <thead className='bg-gray-50 border-green-500 border-l-4 rounded-md'>
                <tr>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Item</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Status</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Due Date</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Template</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Document</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Review/App.</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Lead</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Support</th>
                  <th className='p-6 text-left text-xs font-bold text-gray-500 uppercase tracking-wider'>Vendor</th>
                </tr>
              </thead>
              <tbody className='text-center divide-y rounded-md'>
                <tr className='bg-white'>
                  <td className='text-left p-4'>Initial deal program</td>
                  <td className='p-4'>25%</td>
                  <td className='p-4'>02-05-2022</td>
                  <td className='p-4'>initialDeal.xslx</td>
                  <td>
                    <FileUploadButton changeHandler={changeHandler} />
                  </td>
                  <td className='p-4'>None</td>
                  <td className='p-4'>Theodore Swift</td>
                  <td className='p-4'>Karen Ruecker</td>
                  <td className='p-4'>None</td>
                </tr>
                <tr className='bg-white'>
                  <td className='text-left p-4'>Back of Envelope</td>
                  <td className='p-4'>Not Started</td>
                  <td className='p-4'>03-02-2021</td>
                  <td className='p-4'>backOfEnvelope</td>
                  <td>
                    <FileUploadButton changeHandler={changeHandler} />
                  </td>
                  <td className='p-4'>None</td>
                  <td className='p-4'>Rufus Crooks</td>
                  <td className='p-4'>Elaine Wiza I</td>
                  <td className='p-4'>Santiago Kautzer</td>
                </tr>
                <tr className='bg-white'>
                  <td className='text-left p-4'>Construction Estimate</td>
                  <td className='p-4'>Not Started</td>
                  <td className='p-4'>03-09-2021</td>
                  <td className='p-4'>None</td>
                  <td>
                    <FileUploadButton changeHandler={changeHandler} />
                  </td>
                  <td className='p-4'>None</td>
                  <td className='p-4'>Rene Baumbach</td>
                  <td className='p-4'>Gilbane Builders</td>
                  <td className='p-4'>Gilbane Builders</td>
                </tr>
                <tr className='bg-white'>
                  <td className='text-left p-4'>Concept Drawing</td>
                  <td className='p-4'>75%</td>
                  <td className='p-4'>03-09-2021</td>
                  <td className='p-4'>None</td>
                  <td>
                    <FileUploadButton changeHandler={changeHandler} />
                  </td>
                  <td className='p-4'>None</td>
                  <td className='p-4'>Laurie Harber</td>
                  <td className='p-4'>Smith Architects</td>
                  <td className='p-4'>Smith Architects</td>
                </tr>
                <tr className='bg-white'>
                  <td className='text-left p-4'>D.D Narratives</td>
                  <td className='p-4'>Not Started</td>
                  <td className='p-4'>04-01-2021</td>
                  <td className='p-4'>blankNarratives</td>
                  <td>
                    <FileUploadButton changeHandler={changeHandler} />
                  </td>
                  <td className='p-4'>None</td>
                  <td className='p-4'>Bertha McCullough</td>
                  <td className='p-4'>None</td>
                  <td className='p-4'>None</td>
                </tr>
              </tbody>
            </table>
            <div className='flex bg-gray-50 w-full justify-end py-5 border-t text-neutralColor-normal text-sm'>
              <div className='flex justify-around gap-8 items-end font-semibold px-5'>
                <div>
                  Rows per page:{' '}
                  <select className='bg-white border rounded-md px-2 py-1' onChange={() => {}} defaultValue={10}>
                    <option>5</option>
                    <option>10</option>
                    <option>15</option>
                    <option>20</option>
                  </select>
                </div>
                <div>1 of 5</div>
                <div className='cursor-pointer flex gap-2 items-center hover:text-secondaryColor-normal' id='prev' onClick={() => {}}>
                  <ArrowLeftCircleIcon className='h-6 w-6' />
                  <span>Prev</span>
                </div>
                <div className='cursor-pointer flex gap-2 items-center hover:text-secondaryColor-normal' onClick={() => {}}>
                  <span>Next</span>
                  <ArrowRightCircleIcon className='h-6 w-6' />
                </div>
              </div>
            </div>
          </div>
          <div className='mt-5'>
            <h2 className='font-bold'>Meetings</h2>
            <div className='relative mb-4 w-1/5'>
              <select className='form-input px-2 py-2 appearance-none' name='meetingDate' onChange={changeHandler}>
                <option value='' disabled selected>
                  Select Meeting Date
                </option>
                {meetingDate.map((s, index) => (
                  <option key={index} value={s.date}>
                    {s.date}
                  </option>
                ))}
              </select>
            </div>
            <div className='grid grid-cols-3 gap-2 '>
              <div className='flex flex-col text-sm'>
                <label className='font-bold'>Notes:</label>
                <textarea name='notes' rows={5} cols={20} placeholder='Start taking notes here...' className='border-2 p-2 mt-2' />
              </div>
              <div className='flex flex-col text-sm'>
                <label className='font-bold'>Action Items:</label>
                <textarea
                  name='actionItems'
                  rows={5}
                  cols={20}
                  placeholder='List your action items here...'
                  className='border-2 p-2 mt-2'
                />
              </div>
              <div className='flex flex-col text-sm'>
                <label className='font-bold'>Next Steps:</label>
                <textarea name='nextSteps' rows={5} cols={20} placeholder='List your next steps here...' className='border-2 p-2 mt-2' />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return dashboard;
}
